import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { useAssignments } from '../../hooks/useAssignments';
import { useClasses } from '../../hooks/useClasses';

const MobileBottomNav = ({ currentTab, onTabChange, onActionClick }) => {
  const location = useLocation();
  const { isAdmin, user, profile } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const expandedMenuRef = useRef(null);
  
  const { assignments } = useAssignments({ clientId: user?.uid });
  const { classes } = useClasses({ clientId: user?.uid });

  // Only show navigation on dashboard pages
  const isDashboardPage = location.pathname.includes('/dashboard');
  if (!isDashboardPage) return null;

  // Get unread message count
  useEffect(() => {
    if (!user?.uid) return;

    const userRole = profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : 'client';
    const userAssignmentIds = assignments?.map(a => a.id) || [];
    const userClassIds = classes?.map(c => c.id) || [];
    const userItemIds = [...userAssignmentIds, ...userClassIds];

    const q = query(collection(db, 'comments'), orderBy('created_at', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate() || new Date()
      }));

      // Filter messages based on user role and permissions
      const filteredData = data.filter(comment => {
        if (userRole === 'admin') return true;
        return userItemIds.includes(comment.item_id);
      });

      // Count unread messages
      const unreadCount = filteredData.filter(message => 
        !message.read_by?.includes(user.uid) && message.author_id !== user.uid
      ).length;

      setUnreadMessageCount(unreadCount);
    });

    return () => unsubscribe();
  }, [user?.uid, profile, assignments, classes]);

  // Detect when modals are open
  useEffect(() => {
    const checkForModals = () => {
      const modalSelectors = [
        '[role="dialog"]',
        '.modal-overlay',
        '.fixed.inset-0.bg-black',
        '.fixed.inset-0.bg-gray-600',
        '.ReactModal__Overlay',
        'div[class*="fixed"][class*="inset-0"][class*="bg-black"]',
        'div[class*="fixed"][class*="inset-0"][class*="bg-gray"]'
      ];
      
      const hasOpenModal = modalSelectors.some(selector => {
        const elements = document.querySelectorAll(selector);
        return Array.from(elements).some(el => {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
        });
      });
      
      setIsModalOpen(hasOpenModal);
      if (hasOpenModal) {
        setIsExpanded(false); // Close expanded view when modal opens
      }
    };

    checkForModals();
    
    const observer = new MutationObserver(checkForModals);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    return () => observer.disconnect();
  }, []);

  // Close expanded menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (expandedMenuRef.current && !expandedMenuRef.current.contains(event.target) && isExpanded) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isExpanded]);

  // Navigation items based on user role (tab-based navigation) - iOS Style Icons
  const navItems = [
    {
      id: 'assignments',
      label: 'Assignments',
      icon: 'FileText',
      iosIcon: 'doc.text.fill',
      color: 'text-green-400'
    },
    {
      id: 'classes',
      label: 'Classes',
      icon: 'BookOpen', // Changed to more iOS-like icon
      iosIcon: 'book.fill',
      color: 'text-purple-400'
    },
    {
      id: 'messages',
      label: 'Messages',
      icon: 'MessageCircle', // Changed to more rounded iOS style
      iosIcon: 'message.fill',
      color: 'text-orange-400',
      badge: unreadMessageCount > 0 ? unreadMessageCount : null
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: 'Bell',
      iosIcon: 'bell.fill',
      color: 'text-blue-400'
    }
  ];

  // Get additional navigation items for expanded view
  const getAdditionalNavItems = () => {
    // const baseItems = [
    //   { id: 'profile', label: 'Profile', icon: 'User', color: 'text-blue-400' },
    //   { id: 'settings', label: 'Settings', icon: 'Settings', color: 'text-gray-400' }
    // ];

    if (isAdmin()) {
      return [
        // ...baseItems
        // Removed unused 'users' and 'analytics' items as they are not implemented
      ];
    }
    return []; // baseItems;
  };

  // Get action items for expanded view - iOS Style Icons
  const getActionItems = () => {
    if (isAdmin()) {
      return [
        { id: 'create-user', label: 'Create User', icon: 'UserPlus', action: 'createUser', color: 'text-emerald-400' },
        { id: 'search-comments', label: 'Search', icon: 'Search', action: 'searchComments', color: 'text-yellow-400' },
        { id: 'discord-panel', label: 'Discord', icon: 'MessageCircle', action: 'discordPanel', color: 'text-indigo-400' },
      ];
    }

    return [
      { id: 'submit-assignment', label: 'Submit Work', icon: 'PlusCircle', action: 'submitAssignment', color: 'text-green-400' },
      { id: 'submit-class', label: 'Add Class', icon: 'BookOpen', action: 'submitClass', color: 'text-purple-400' },
      { id: 'discord-settings', label: 'Discord', icon: 'MessageCircle', action: 'discordPanel', color: 'text-indigo-400' },
    ];
  };

  const additionalNavItems = getAdditionalNavItems();
  const actionItems = getActionItems();

  const isActive = (tabId) => {
    return currentTab === tabId;
  };

  const handleTabChange = (tabId) => {
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  const handleActionClick = (action) => {
    if (onActionClick) {
      onActionClick(action);
    }
    setIsExpanded(false); // Close expanded view after action
  };

  // Hide navigation when modal is open
  if (isModalOpen) return null;

  // Only show on mobile
  return (
    <motion.nav
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="fixed bottom-0 left-0 right-0 z-50 lg:hidden"
    >
      {/* Expanded Panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            ref={expandedMenuRef}
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-24 left-3 right-3 bg-white/15 backdrop-filter backdrop-blur-xl border border-white/25 rounded-3xl shadow-2xl"
          >
            <div className="p-4">
              {/* Additional Navigation Items */}
              {additionalNavItems.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-white/70 text-xs uppercase tracking-wider font-semibold mb-3 px-2">Navigation</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {additionalNavItems.map((item) => (
                      <motion.button
                        key={item.id}
                        onClick={() => {
                          handleTabChange(item.id);
                          setIsExpanded(false);
                        }}
                        className={`
                          flex flex-col items-center justify-center
                          px-3 py-3 rounded-xl transition-all duration-200
                          ${isActive(item.id) 
                            ? 'bg-white/20 shadow-sm' 
                            : 'hover:bg-white/10'
                          }
                        `}
                        whileTap={{ scale: 0.95 }}
                      >
                        <div className={`
                          transition-colors duration-200
                          ${isActive(item.id) ? item.color : 'text-white/60'}
                        `}>
                          <Icon 
                            name={item.icon} 
                            size={20}
                            strokeWidth={2.5}
                            className="transition-all duration-200" 
                            style={{
                              fill: isActive(item.id) ? 'currentColor' : 'none',
                              strokeLinecap: 'round',
                              strokeLinejoin: 'round'
                            }}
                          />
                        </div>
                        <span className="text-xs font-medium mt-1 text-white/80">
                          {item.label}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Items */}
              {actionItems.length > 0 && (
                <div>
                  <h3 className="text-white/70 text-xs uppercase tracking-wider font-semibold mb-3 px-2">Quick Actions</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {actionItems.map((item) => (
                      <motion.button
                        key={item.id}
                        onClick={() => handleActionClick(item.action)}
                        className="flex flex-col items-center justify-center px-3 py-3 rounded-xl transition-all duration-200 hover:bg-white/10"
                        whileTap={{ scale: 0.95 }}
                      >
                        <div className={`transition-colors duration-200 ${item.color}`}>
                          <Icon 
                            name={item.icon} 
                            size={20}
                            strokeWidth={2.5}
                            className="transition-all duration-200" 
                            style={{
                              strokeLinecap: 'round',
                              strokeLinejoin: 'round'
                            }}
                          />
                        </div>
                        <span className="text-xs font-medium mt-1 text-white/80 text-center">
                          {item.label}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Navigation Bar - iOS Style */}
      <div className="mx-3 mb-4 rounded-3xl bg-white/15 backdrop-filter backdrop-blur-xl border border-white/25 shadow-xl">
        <div className="flex items-center justify-around px-3 py-4">
          {navItems.slice(0, 3).map((item, index) => {
            const active = isActive(item.id);
            
            return (
              <motion.button
                key={item.id}
                onClick={() => handleTabChange(item.id)}
                className={`
                  relative flex flex-col items-center justify-center
                  px-4 py-3 rounded-2xl transition-all duration-200
                  ${active 
                    ? 'bg-white/25 shadow-lg transform scale-105' 
                    : 'hover:bg-white/10'
                  }
                `}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className={`
                  relative transition-colors duration-200
                  ${active ? item.color : 'text-white/60'}
                `}>
                  <Icon 
                    name={item.icon} 
                    size={active ? 22 : 20}
                    strokeWidth={active ? 2.5 : 2}
                    className="transition-all duration-200" 
                    style={{
                      fill: active ? 'currentColor' : 'none',
                      strokeLinecap: 'round',
                      strokeLinejoin: 'round'
                    }}
                  />
                  {item.badge && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] h-[18px] flex items-center justify-center shadow-sm">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                
                <span className={`
                  text-xs font-medium mt-1 transition-colors duration-200
                  ${active ? 'text-white' : 'text-white/50'}
                `}>
                  {item.label}
                </span>
                
                {active && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-primary rounded-full opacity-80"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 0.8 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}

          {/* More Button */}
          <motion.button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`
              relative flex flex-col items-center justify-center
              px-4 py-3 rounded-2xl transition-all duration-200
              ${isExpanded 
                ? 'bg-white/25 shadow-lg transform scale-105' 
                : 'hover:bg-white/10'
              }
            `}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <motion.div 
              className={`transition-colors duration-200 ${isExpanded ? 'text-primary' : 'text-white/60'}`}
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <Icon 
                name="ChevronUp" 
                size={isExpanded ? 22 : 20}
                strokeWidth={2.5}
                className="transition-all duration-200" 
                style={{
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round'
                }}
              />
            </motion.div>
            
            <span className={`
              text-xs font-medium mt-1 transition-colors duration-200
              ${isExpanded ? 'text-white' : 'text-white/50'}
            `}>
              More
            </span>
            
            {isExpanded && (
              <motion.div
                layoutId="activeTab"
                className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-primary rounded-full opacity-80"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 0.8 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
            )}
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

export default MobileBottomNav;